import React, { Fragment, lazy, Suspense, useEffect } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { loadProgressBar } from 'axios-progress-bar'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import api from './axios'
import { actions, initApp } from './Redux/slices/init'
import { getSectionTitle } from './config'
import './style.main.scss'
import './themeStyles/var.css'
import TopBar from './topbar/TopBar'
import AcceptModal from './UserSettings/Preferences/AcceptModal'
import SettingsModal from './UserSettings/Preferences/SettingsModal'
import Cookies from 'universal-cookie'
import * as Sentry from '@sentry/react'
import { Context } from './helpers/context/Context'
import LoadingArea from './Loaders/LoadingArea'
import { selectPreferences } from './Redux/selectors/selectors'
import EmptyData from './EmptyData/EmptyData'

function RouteAdvanced({ title, isPrivate = false, componentProps, component: Component, layout, ...rest }) {
	const dispatch = useDispatch()
	const isAuthenticated = !!localStorage.getItem('user')
	const { isModified, isSettingsModalOpen, isAcceptModalVisible } = useSelector((state) => state.preferences)
	const withBlock = window.location.pathname !== '/settings/preferences'
	const shouldBlock = withBlock && isModified && !isSettingsModalOpen && !(withBlock && isAcceptModalVisible)
	const Layout = layout || Fragment
	const cookies = new Cookies()

	const expires = new Date()
	expires.setMonth(expires.getMonth() + 1) // 1 month

	if (isAuthenticated) {
		cookies.set('isAuthorization', 'true', { path: '/', domain: 'gtobase.com', expires })
	}

	// if (isModified) {
	// 	dispatch(setCurrentSection('preferences'))
	// }

	return (
		<Route
			{...rest}
			render={(props) => (
				<Layout>
					<Helmet>
						<title>{getSectionTitle(title)}</title>
					</Helmet>
					{/* {shouldBlock ? ( */}
					{/* 	<Redirect push to='/settings/preferences'> */}
					{/* 		{dispatch(setRedirectedPoint(rest.path))} */}
					{/* 	</Redirect> */}
					{/* ) :  */}
					{isPrivate && !isAuthenticated ? (
						<Redirect to={{ pathname: '/', state: { from: props.location } }} />
					) : (
						<Component {...componentProps} {...props} />
					)}
				</Layout>
			)}
		/>
	)
}

const routes = [
	{
		path: '/',
		exact: true,
		component: lazy(() => import('./AuthPage/AuthPage')),
		title: 'Main Page',
	},
	{
		path: '/prices',
		exact: true,
		component: lazy(() => import('./pages/Prices/Prices')),
		title: 'Prices',
	},
	{
		path: '/password_reset',
		exact: true,
		component: lazy(() => import('./AuthPage/AuthPage')),
		title: 'Reset Password',
		componentProps: { isConfirmReset: true },
	},
	{
		path: '/email_activated',
		exact: true,
		component: lazy(() => import('./AuthPage/AuthPage')),
		title: 'Email activated',
		componentProps: { isUserConfirmed: true },
	},
	{
		path: '/dashboard',
		exact: false,
		component: lazy(() => import('./MainPage/MainPage')),
		isPrivate: true,
		title: 'GTO Solutions for Poker by SimplePoker',
	},
	{
		exact: true,
		path: '/docs/terms',
		component: lazy(() => import('./Documents/Documents')),
		title: 'Terms',
		componentProps: { current: 'terms' },
	},
	{
		exact: true,
		path: '/docs/refund',
		component: lazy(() => import('./Documents/Documents')),
		title: 'Refund',
		componentProps: { current: 'refund' },
	},
	{
		exact: true,
		path: '/hands',
		component: lazy(() => import('./Hands/Hands')),
		isPrivate: true,
		title: 'Hands',
	},
	{
		exact: true,
		path: '/trainer',
		component: lazy(() => import('./Trainer')),
		isPrivate: true,
		title: 'Trainer',
	},
	{
		exact: true,
		path: '/viewer',
		component: lazy(() => import('./Viewer/Viewer')),
		isPrivate: true,
		title: 'Viewer',
	},
	{
		exact: true,
		path: '/upload',
		component: lazy(() => import('./Upload/UploadControl')),
		isPrivate: true,
		title: 'Upload',
	},
	{
		exact: true,
		path: '/settings',
		component: lazy(() => import('./UserSettings/UserSettings')),
		isPrivate: true,
		title: 'Settings',
	},
	{
		exact: true,
		path: '/library',
		component: lazy(() => import('./Library/Library')),
		isPrivate: true,
		title: 'Library',
	},
	{
		exact: true,
		path: '/settings/subscriptions',
		component: lazy(() => import('./UserSettings/Licenses/Licenses')),
		layout: lazy(() => import('./UserSettings/UserSettings')),
		isPrivate: true,
		title: 'Subscription',
	},
	{
		exact: true,
		path: '/settings/aliases',
		component: lazy(() => import('./UserSettings/Aliases/Aliases')),
		layout: lazy(() => import('./UserSettings/UserSettings')),
		isPrivate: true,
		title: 'Aliases',
	},
]

export const renderRoutes = (routess = []) => (
	<Suspense fallback={<></>}>
		<Switch>
			{routess.map((route) => {
				const { layout, component, exact, title, componentProps, isPrivate, path } = route

				return (
					<RouteAdvanced
						key={path}
						exact={exact}
						path={path}
						component={component}
						title={title}
						componentProps={componentProps}
						isPrivate={isPrivate}
						layout={layout}
					/>
				)
			})}

			<Route path='*'>
				<Redirect from='*' to='/' />
			</Route>
		</Switch>
	</Suspense>
)

function App() {
	loadProgressBar({ showSpinner: false }, api.service)
	const dispatch = useDispatch()
	const { isAppReady } = useSelector((state) => state.init)
	const user = localStorage.getItem('user')

	useEffect(() => {
		if (!isAppReady && user) {
			dispatch(initApp())
		} else if (!isAppReady && !user) {
			dispatch(actions.setAppReady(true))
		}
	}, [dispatch])

	const errorHandler = ({ error, componentStack, resetError }) => {
		return (
			<EmptyData
				text={'Oops, something went wrong...'}
				addedText={"GTOBase Team is already doing our best to fix the issue. Please try to reload the app"}
				isErrorHandler
				refreshPage={resetError}
			/>
		)
	}

	return (
		<div className='App'>
			{!isAppReady ? (
				<></>
			) : (
				<Sentry.ErrorBoundary fallback={errorHandler}>
					<TopBar />

					<AcceptModal />

					<SettingsModal />

					<div className='Content'>
						<Context>{renderRoutes(routes)}</Context>
					</div>
				</Sentry.ErrorBoundary>
			)}
		</div>
	)
}

export default Sentry.withProfiler(App, { includeRender: false, includeUpdates: false })
